"use client";

/**
 * SceneHeader — unified scrolltell scene anchor.
 *
 * Two visual variants supported:
 *   - "dual-rail" (Niche v3 default): "── NN · TITLE ──" — gradient rails on BOTH sides
 *   - "right-rail" (Plan/Brand legacy): "NN [rail] TITLE" — rail only on right
 *
 * Renders both `index` (legacy Plan/Brand) and `number` (Niche v3 new API) — `number` wins.
 *
 * Promoted (Session 43) from forks:
 *   - PlanShared.SceneHeader        → right-rail layout, no fade-up motion (static), `index` prop
 *   - niche-v3/.../SceneHeader      → dual-rail layout, viewport fade-up, `number` prop, `accent` token
 *
 * Typography: text-[12px] (dual-rail) / text-[10-11px] (right-rail), uppercase tracking-[0.2em],
 * font-semibold text-white/60 (or /50 for right-rail to match legacy).
 *
 * a11y: heading element controlled via `as` prop (default h2).
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

type SceneHeaderVariant = "dual-rail" | "right-rail";

interface SceneHeaderProps {
  /** Scene number (1-based). Padded to 2 digits. New API. */
  number?: number;
  /** Legacy alias for `number` — Plan/Brand callers. If both, `number` wins. */
  index?: number;
  /** Scene title — uppercased by typography. */
  title: string;
  /** Visual layout. Default "dual-rail" (Niche v3 style). */
  variant?: SceneHeaderVariant;
  /**
   * Tailwind gradient classes for the accent rail(s).
   * Default amber/35 → amber/10 → transparent.
   */
  accent?: string;
  /** Extra wrapper classes. */
  className?: string;
  /** Heading tag — default h2. */
  as?: "h1" | "h2" | "h3";
  /** Optional id for aria-labelledby on parent sections. */
  id?: string;
}

export function SceneHeader({
  number,
  index,
  title,
  variant = "dual-rail",
  accent,
  className,
  as: Tag = "h2",
  id,
}: SceneHeaderProps) {
  const reduced = useReducedMotion();
  const n = number ?? index ?? 0;
  const padded = String(n).padStart(2, "0");

  if (variant === "right-rail") {
    // Legacy Plan/Brand layout — static (no fade-up), single right rail.
    const railGradient =
      accent ?? "from-amber-500/30 via-amber-500/10 to-transparent";
    return (
      <div
        id={id}
        className={cn("mb-10 flex items-center gap-3", className)}
      >
        <span
          aria-hidden
          className="font-heading text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400/60"
        >
          {padded}
        </span>
        <div
          aria-hidden
          className={cn("h-px flex-1 bg-gradient-to-r", railGradient)}
        />
        <Tag className="font-heading text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 m-0">
          {title}
        </Tag>
      </div>
    );
  }

  // Dual-rail (Niche v3 default): rails on BOTH sides, viewport fade-up.
  const railGradient =
    accent ?? "from-amber-400/35 via-amber-400/10 to-transparent";

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.5, ease: "easeOut" }}
      className={cn(
        "mb-10 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-white/60",
        className,
      )}
    >
      {/* Left rail (mirrored — gradient fades outward) */}
      <span
        aria-hidden
        className={cn("h-px w-10 bg-gradient-to-l", railGradient)}
      />

      {/* NN · TITLE */}
      <Tag id={id} className="m-0 flex items-center gap-2 font-heading">
        <span aria-hidden className="text-amber-400/70 tabular-nums">
          {padded}
        </span>
        <span aria-hidden className="text-white/30">
          ·
        </span>
        <span>{title}</span>
      </Tag>

      {/* Right rail — takes remaining space */}
      <span
        aria-hidden
        className={cn("h-px flex-1 bg-gradient-to-r", railGradient)}
      />
    </motion.div>
  );
}
